
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import * as React from 'react';
import { useRouter } from 'next/router';
import { GetStaticPaths, NextPage } from 'next';
import { BreadcrumbJsonLd, NextSeo } from 'next-seo';
import Stack from '@mui/material/Stack';
import ResponsiveContainer from 'components/atomic/organisms/responsive-container';
import MarkdownViewer from 'components/atomic/organisms/markdown-viewer';
import RightPanel from 'pages/wiki-detail/right-panel';
import CourseCard from 'components/atomic/molecules/course-card';
import PathCard from 'components/atomic/molecules/path-card/v2';
import ItemList from 'pages/wiki-detail/item-list';
import MainLayout from 'components/layouts/main-layout';
import Hero from 'pages/wiki-detail/hero';
import Routes, { toAbsoluteUrl } from 'utils/routes';
import { Role as RoleType } from 'types/wiki';
import devvSdk from 'utils/devv-sdk';
import Head from 'next/head';
const SEO = ({ role }: {
    role: RoleType;
}) => {
    const router = useRouter();
    const keywords = [
        'professioni di programmazione',
        'lavoro da programmatore',
        'lavoro da sviluppatore',
        role.title,
        ...role.categoriesTitles,
    ];
    const OGTitle = `${role.title}: Chi è e cosa fa?`;
    const description = `Chi è un ${role.title}? Cosa fa e quanto viene retribuito un ${role.title}? Scopri tutti i dettagli delle professione da ${role.title}: 
${role.description}`;
    const minSalary = parseInt(role.salary.split('-')[0].replace('k', '000'));
    const maxSalary = parseInt(role.salary.split('-')[1].replace('k', '000'));
    return (<>
            <NextSeo title={role.title} titleTemplate={`Chi è un %s e di cosa si occupa? | Devv`} description={description} canonical={`${toAbsoluteUrl(router.asPath)}`} additionalMetaTags={[
            {
                property: 'keywords',
                content: keywords.join(', '),
            },
        ]} openGraph={{
            url: `${toAbsoluteUrl(router.asPath)}`,
            images: [
                {
                    url: Routes.OGImageTitleCTA(OGTitle, 'DEVV WIKI'),
                    secureUrl: Routes.OGImageTitleCTA(OGTitle, 'DEVV WIKI'),
                    type: 'image/png',
                    width: 1200,
                    height: 630,
                },
            ],
        }}/>
            <BreadcrumbJsonLd itemListElements={[
            {
                position: 1,
                name: 'Wiki',
                item: `${toAbsoluteUrl(Routes.Wiki)}`,
            },
            {
                position: 2,
                name: 'Professioni',
                item: `${toAbsoluteUrl(Routes.WikiRoles)}`,
            },
            {
                position: 3,
                name: role.title,
                item: `${toAbsoluteUrl(Routes.WikiRole(role.slug))}`,
            },
        ]}/>
            <Head>
                <script type="application/ld+json" dangerouslySetInnerHTML={{
            __html: `{
      "@context": "https://schema.org/",
      "@type": "Occupation",
      "name": "${role.title}",
      "description": "${role.description}",
      "estimatedSalary": [
        {
          "@type": "MonetaryAmountDistribution",
          "name": "base",
          "currency": "USD",
          "duration": "P1Y",
          "minValue": "${minSalary}",
          "maxValue": "${maxSalary}"
        }
      ],
      "occupationLocation": [
        {
          "@type": "Country",
          "name": "IT"
        }
      ]
    }
  `,
        }} key="product-jsonld"/>
            </Head>
        </>);
};
interface RolePageProps {
    role: RoleType;
}
const Role: NextPage<RolePageProps> = ({ role }) => {
    return (<>
            <SEO role={role}/>

            <MainLayout isFluid>
                <Hero title={role.title} description={role.description} categoriesTitles={role.categoriesTitles} breadcrumbs={[
            {
                title: 'Wiki',
                href: undefined,
            },
            {
                title: 'Professioni',
                href: Routes.WikiRoles,
            },
            {
                title: role.title,
                href: undefined,
            },
        ]} color="#7f3991"/>

                <ResponsiveContainer maxWidth={'lg'}>
                    <Stack direction={'row'} paddingY={2}>
                        <Stack flexGrow={1}>
                            <MarkdownViewer>{role.body}</MarkdownViewer>

                            {role.courses.length ? (<ItemList title={`Corsi per un ${role.title}`} items={role.courses} itemColumns={{ xs: 12, sm: 6, md: 4, lg: 6 }} mt={4} renderItem={(item) => <CourseCard {...item}/>}/>) : null}

                            {role.paths.length ? (<ItemList title={`Percorsi per diventare ${role.title}`} items={role.paths} mt={4} itemColumns={{ xs: 12, sm: 6, md: 4, lg: 6 }} renderItem={(item) => <PathCard {...item}/>}/>) : null}
                        </Stack>

                        <RightPanel />
                    </Stack>
                </ResponsiveContainer>
            </MainLayout>
        </>);
};
export const getStaticPaths: GetStaticPaths = async () => {
    const { data } = await devvSdk.getRolesOverview();
    return {
        paths: data.map((el) => ({
            params: { slug: el.slug },
        })),
        fallback: false,
    };
};
const getStaticProps = async ({ params }: {
    params: {
        slug: string;
    };
}) => {
    const { data: role } = await devvSdk.getRoleBySlug(params.slug);
    return {
        props: {
            role,
        },
    };
};
export default Role;

    async function __Next_Translate__getStaticProps__18916ceb2d9__(ctx) {
      const res = await getStaticProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/wiki/roles/[slug]',
            loaderName: 'getStaticProps',
            loadLocaleFrom: (l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__18916ceb2d9__ as getStaticProps }
  